/* ── StreamProgress ──────────────────────────────────────────
   Multi-phase progress indicator shown during the 30-47s
   response wait time. Phases advance based on SSE events:
     1. "Searching corpus…"      → query sent
     2. "Retrieving clauses…"    → citations event received
     3. "Generating response…"   → first text chunk
     4. (hidden — streaming text is visible)

   Style: --ink-3 colour, --font-mono, --t-sm size, pulsing dot
   ────────────────────────────────────────────────────────────── */

export type StreamPhase = 'searching' | 'retrieving' | 'generating' | 'streaming' | 'idle';

interface StreamProgressProps {
  phase: StreamPhase;
}

const PHASE_LABELS: Record<StreamPhase, string> = {
  searching: 'Searching corpus',
  retrieving: 'Retrieving clauses',
  generating: 'Generating response',
  streaming: '',
  idle: '',
};

/* ── Inline styles (no .css touching) ────────────────────────── */

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 0 4px',
  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
  fontSize: 'var(--t-sm, 13px)',
  color: 'var(--ink-3, #94a3b8)',
  userSelect: 'none',
  minHeight: '28px',
};

const dotStyle: React.CSSProperties = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: 'var(--ink-3, #94a3b8)',
  animation: 'stream-progress-pulse 1.4s ease-in-out infinite',
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  letterSpacing: '0.02em',
};

/* Phases chevron — subtle step indicator */
const stepsContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: '8px',
};

const stepDotBase: React.CSSProperties = {
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  transition: 'background-color 0.3s ease, opacity 0.3s ease',
};

const PHASE_ORDER: StreamPhase[] = ['searching', 'retrieving', 'generating'];

export default function StreamProgress({ phase }: StreamProgressProps) {
  if (phase === 'idle' || phase === 'streaming') return null;

  const label = PHASE_LABELS[phase];
  const currentIdx = PHASE_ORDER.indexOf(phase);

  return (
    <>
      {/* Inject keyframes via a <style> tag (no .css file needed) */}
      <style>{`
        @keyframes stream-progress-pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.85); }
          50% { opacity: 1; transform: scale(1.15); }
        }
      `}</style>
      <div style={containerStyle} role="status" aria-live="polite" aria-label={label}>
        <span style={dotStyle} aria-hidden="true" />
        <span style={labelStyle}>{label}…</span>
        <span style={stepsContainerStyle} aria-hidden="true">
          {PHASE_ORDER.map((p, i) => (
            <span
              key={p}
              style={{
                ...stepDotBase,
                backgroundColor: i <= currentIdx
                  ? 'var(--ink-3, #94a3b8)'
                  : 'var(--ink-3, #94a3b8)',
                opacity: i <= currentIdx ? 1 : 0.25,
              }}
            />
          ))}
        </span>
      </div>
    </>
  );
}
